/**
 * 第一幕半固定卡牌定义 — 30张
 *
 * 每局从中抽取15张组成本局牌库。
 * 卡牌效果固定，但LLM负责根据角色性格推演具体对话反应。
 */
import type { Act1Card } from '../types/cards';

// ============================================
// 表现卡（10张，针对主角）
// ============================================
const PERFORMANCE_CARDS: Act1Card[] = [
  {
    id: 'a1_perf_01',
    name: '展示厨艺',
    type: '表现卡',
    description: '让主角聊起自己的烹饪经历，展示生活能力',
    hint: '传统家长特别吃这套',
    attitudeEffect: 15,
    affectionEffect: 3,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '家长关注点为"条件"', modifier: 5, description: '务实型家长额外加分' },
    ],
  },
  {
    id: 'a1_perf_02',
    name: '聊工作成就',
    type: '表现卡',
    description: '让主角谈谈工作上的成绩，展示上进心',
    hint: '父亲类型的亲友会特别关注',
    attitudeEffect: 10,
    affectionEffect: 5,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '对方父亲在场', modifier: 5, description: '父亲额外认可' },
    ],
  },
  {
    id: 'a1_perf_03',
    name: '谈家庭观念',
    type: '表现卡',
    description: '让主角表达对家庭的重视和规划',
    hint: '保守型家长加分多，开放型效果一般',
    attitudeEffect: 12,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '家长开放度为"保守"', modifier: 8, description: '传统家长大幅加分' },
      { condition: '家长开放度为"开放"', modifier: -5, description: '开放家长觉得太传统' },
    ],
  },
  {
    id: 'a1_perf_04',
    name: '秀兴趣爱好',
    type: '表现卡',
    description: '让主角分享自己的兴趣爱好',
    hint: '如果和家长兴趣重合效果翻倍',
    attitudeEffect: 8,
    comfortEffect: 5,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '与家长兴趣重合', modifier: 7, description: '找到共同话题' },
    ],
  },
  {
    id: 'a1_perf_05',
    name: '适度自黑',
    type: '表现卡',
    description: '让主角用幽默的方式自嘲，拉近距离',
    hint: '开放型家长喜欢，保守型可能觉得不稳重',
    attitudeEffect: 10,
    comfortEffect: 5,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '家长开放度为"开放"', modifier: 5, description: '觉得很有趣' },
      { condition: '家长开放度为"保守"', modifier: -5, description: '觉得不够稳重' },
    ],
  },
  {
    id: 'a1_perf_06',
    name: '展示孝心',
    type: '表现卡',
    description: '让主角提起对父母的关心和照顾',
    hint: '几乎所有家长都吃这套',
    attitudeEffect: 12,
    targetType: 'protagonist',
  },
  {
    id: 'a1_perf_07',
    name: '聊未来规划',
    type: '表现卡',
    description: '让主角谈谈对未来的规划和目标',
    hint: '有目标感的人更受家长青睐',
    attitudeEffect: 10,
    affectionEffect: 3,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '家长关注点为"条件"', modifier: 5, description: '务实型家长额外认可' },
    ],
  },
  {
    id: 'a1_perf_08',
    name: '主动倒茶',
    type: '表现卡',
    description: '让主角主动为在座的人倒茶添水，展示礼貌',
    hint: '小细节大加分',
    attitudeEffect: 8,
    comfortEffect: 3,
    targetType: 'protagonist',
  },
  {
    id: 'a1_perf_09',
    name: '真诚赞美',
    type: '表现卡',
    description: '让主角真诚地赞美对方或对方家人',
    hint: '真诚是关键，过度会适得其反',
    attitudeEffect: 10,
    affectionEffect: 5,
    targetType: 'protagonist',
  },
  {
    id: 'a1_perf_10',
    name: '展示才艺',
    type: '表现卡',
    description: '让主角展示一项特长或才艺',
    hint: '有才华的人总是加分的',
    attitudeEffect: 12,
    affectionEffect: 5,
    comfortEffect: 3,
    targetType: 'protagonist',
  },
];

// ============================================
// 助攻卡（8张，针对亲友）
// ============================================
const ASSIST_CARDS: Act1Card[] = [
  {
    id: 'a1_asst_01',
    name: '母亲助攻',
    type: '助攻卡',
    description: '暗示己方母亲帮忙说好话',
    hint: '母亲影响力高时效果更好',
    attitudeEffect: 20,
    targetType: 'family',
    conditionalEffects: [
      { condition: '母亲开放度为"保守"', modifier: -10, description: '保守母亲不太配合' },
    ],
  },
  {
    id: 'a1_asst_02',
    name: '父亲认可',
    type: '助攻卡',
    description: '让对方父亲对主角表示认可',
    hint: '父亲的认可分量很重',
    attitudeEffect: 15,
    targetType: 'family',
  },
  {
    id: 'a1_asst_03',
    name: '闺蜜打圆场',
    type: '助攻卡',
    description: '让闺蜜帮忙化解尴尬，活跃气氛',
    hint: '全场态度都会小幅提升',
    attitudeEffect: 5,
    comfortEffect: 8,
    targetType: 'all',
  },
  {
    id: 'a1_asst_04',
    name: '兄弟捧场',
    type: '助攻卡',
    description: '让兄弟帮忙活跃气氛、夸赞主角',
    hint: '可能过头，有翻车风险',
    attitudeEffect: 10,
    comfortEffect: 5,
    targetType: 'family',
    conditionalEffects: [
      { condition: '家长开放度为"保守"', modifier: -8, description: '保守家长觉得太闹' },
    ],
  },
  {
    id: 'a1_asst_05',
    name: '亲友背书',
    type: '助攻卡',
    description: '让一位亲友为主角的人品做担保',
    hint: '影响力高的亲友效果更好',
    attitudeEffect: 12,
    targetType: 'family',
    conditionalEffects: [
      { condition: '亲友影响力为"高"', modifier: 8, description: '高影响力亲友背书更有说服力' },
    ],
  },
  {
    id: 'a1_asst_06',
    name: '共同回忆',
    type: '助攻卡',
    description: '让亲友分享和主角的温馨回忆',
    hint: '侧面展示主角的好品质',
    attitudeEffect: 10,
    affectionEffect: 3,
    targetType: 'family',
  },
  {
    id: 'a1_asst_07',
    name: '暗中观察',
    type: '助攻卡',
    description: '让亲友暗中观察对方的细节并私下汇报',
    hint: '获取情报，为后续出牌提供参考',
    attitudeEffect: 0,
    comfortEffect: 5,
    targetType: 'family',
  },
  {
    id: 'a1_asst_08',
    name: '缓和气氛',
    type: '助攻卡',
    description: '让亲友讲个笑话或转移话题，缓和紧张气氛',
    hint: '适合在气氛僵硬时使用',
    attitudeEffect: 5,
    comfortEffect: 10,
    targetType: 'all',
  },
];

// ============================================
// 化解卡（6张，应对危机）
// ============================================
const RESOLVE_CARDS: Act1Card[] = [
  {
    id: 'a1_rslv_01',
    name: '幽默化解',
    type: '化解卡',
    description: '用幽默的方式化解尴尬局面',
    hint: '消除负面事件的影响',
    attitudeEffect: 5,
    comfortEffect: 8,
    targetType: 'all',
  },
  {
    id: 'a1_rslv_02',
    name: '真诚解释',
    type: '化解卡',
    description: '真诚地解释误会，挽回印象',
    hint: '可以逆转较大的负面态度',
    attitudeEffect: 15,
    targetType: 'protagonist',
  },
  {
    id: 'a1_rslv_03',
    name: '转移话题',
    type: '化解卡',
    description: '巧妙地转移到安全话题',
    hint: '止损但可能被认为逃避',
    attitudeEffect: 3,
    comfortEffect: 5,
    targetType: 'all',
    conditionalEffects: [
      { condition: '家长关注点为"细节"', modifier: -5, description: '细节型家长觉得在逃避' },
    ],
  },
  {
    id: 'a1_rslv_04',
    name: '以退为进',
    type: '化解卡',
    description: '先承认不足，再展示改进的决心',
    hint: '谦虚的态度往往能赢得好感',
    attitudeEffect: 10,
    affectionEffect: 3,
    targetType: 'protagonist',
  },
  {
    id: 'a1_rslv_05',
    name: '求助亲友',
    type: '化解卡',
    description: '向己方亲友求助，让他们帮忙解围',
    hint: '亲友关系好时效果更佳',
    attitudeEffect: 8,
    comfortEffect: 5,
    targetType: 'family',
  },
  {
    id: 'a1_rslv_06',
    name: '坦然面对',
    type: '化解卡',
    description: '不回避问题，坦然面对质疑',
    hint: '展示成熟和自信',
    attitudeEffect: 12,
    comfortEffect: 3,
    targetType: 'protagonist',
  },
];

// ============================================
// 事件卡（6张，影响全场）
// ============================================
const EVENT_CARDS: Act1Card[] = [
  {
    id: 'a1_evnt_01',
    name: '亲戚到访',
    type: '事件卡',
    description: '一位不速之客突然到访，打乱节奏',
    hint: '制造混乱，双方家长态度小幅下降',
    attitudeEffect: -5,
    targetType: 'all',
  },
  {
    id: 'a1_evnt_02',
    name: '电话打断',
    type: '事件卡',
    description: '一通电话打断了当前话题',
    hint: '重置当前话题，可以打断尴尬局面',
    attitudeEffect: 0,
    comfortEffect: 5,
    targetType: 'all',
  },
  {
    id: 'a1_evnt_03',
    name: '服务员失误',
    type: '事件卡',
    description: '服务员不小心打翻了东西',
    hint: '考验主角应对能力，处理好大加分',
    attitudeEffect: 10,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '主角性格包含"温柔"或"暖心"', modifier: 5, description: '温柔处理额外加分' },
    ],
  },
  {
    id: 'a1_evnt_04',
    name: '孩子闯入',
    type: '事件卡',
    description: '邻桌的小孩跑过来捣乱',
    hint: '看主角对孩子的态度，家长很在意这个',
    attitudeEffect: 8,
    targetType: 'protagonist',
    conditionalEffects: [
      { condition: '家长关注点为"条件"', modifier: 5, description: '务实家长看重对孩子的态度' },
    ],
  },
  {
    id: 'a1_evnt_05',
    name: '老照片',
    type: '事件卡',
    description: '有人翻出了主角小时候的照片',
    hint: '可能尴尬也可能温馨',
    attitudeEffect: 5,
    comfortEffect: 8,
    affectionEffect: 3,
    targetType: 'all',
  },
  {
    id: 'a1_evnt_06',
    name: '突然停电',
    type: '事件卡',
    description: '突然停电了，场面一度混乱',
    hint: '黑暗中的反应最真实',
    attitudeEffect: 0,
    comfortEffect: -5,
    targetType: 'all',
    conditionalEffects: [
      { condition: '主角性格包含"幽默"', modifier: 10, description: '用幽默化解尴尬' },
    ],
  },
];

/** 完整的第一幕卡牌池（30张） */
export const ACT1_CARD_POOL: Act1Card[] = [
  ...PERFORMANCE_CARDS,
  ...ASSIST_CARDS,
  ...RESOLVE_CARDS,
  ...EVENT_CARDS,
];

/**
 * 从卡牌池中随机抽取指定数量的卡牌
 * 确保类型分布合理
 */
export function drawAct1Cards(count: number, seed?: number): Act1Card[] {
  const rng = seed !== undefined
    ? () => { seed = (seed! * 16807) % 2147483647; return (seed - 1) / 2147483646; }
    : Math.random;

  // 按类型分组
  const byType: Record<string, Act1Card[]> = {
    '表现卡': [...PERFORMANCE_CARDS],
    '助攻卡': [...ASSIST_CARDS],
    '化解卡': [...RESOLVE_CARDS],
    '事件卡': [...EVENT_CARDS],
  };

  // 保证每种类型至少抽到一定数量
  const result: Act1Card[] = [];
  const minPerType = Math.max(1, Math.floor(count / 4));

  for (const [, cards] of Object.entries(byType)) {
    const shuffled = cards.sort(() => rng() - 0.5);
    result.push(...shuffled.slice(0, minPerType));
  }

  // 剩余名额从全池随机补充
  const remaining = ACT1_CARD_POOL.filter(c => !result.includes(c));
  const shuffledRemaining = remaining.sort(() => rng() - 0.5);
  const needed = count - result.length;
  result.push(...shuffledRemaining.slice(0, needed));

  return result.sort(() => rng() - 0.5);
}
